import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../apiClient';
import { AutoGrowTextarea, ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { displayDate, toDateInput, todayDateInput } from './registerFormat';
import {
  FilterCheckbox,
  FilterCount,
  MultiSelect,
  SearchBox,
  anyFilterSet,
  matchesSelection,
} from './registerFilters';
import { useNavGuardConfirm, useRegisterDirty } from '../unsavedChangesGuard';

// Re-exported so CustomerProjectRegisters.js and this file's tests keep importing them from here --
// the implementations live in registerFormat.js/registerFilters.js now that all four registers need
// them.
export { displayDate, toDateInput };
export { FilterCount };

// Mirrors RAAIDD_TYPES/RAAIDD_STATUSES/RAAIDD_LEVEL_LABELS in packages/shared -- apps/web can't
// import it (ADR 0019), and this vocabulary is small and stable enough not to be worth a network
// round trip just to populate a dropdown.
export const RAAIDD_TYPES = ['Risk', 'Action', 'Assumption', 'Issue', 'Dependency', 'Decision'];
export const RAAIDD_STATUSES = ['Not Started', 'Open', 'In Progress', 'Monitoring', 'Completed', 'Closed', 'Canceled'];
export const RAAIDD_LEVEL_LABELS = ['Low', 'Medium', 'High'];

/**
 * The only two types that carry a risk rating. An Assumption, Action, Dependency or Decision has
 * nothing to rate (Jeremy, 2026-09-03). `buildPayload` clears the three rating fields when the type
 * isn't one of these on save, so reclassifying an Issue to an Action and saving genuinely removes the
 * rating rather than leaving it stored and invisible.
 */
export const RATED_TYPES = new Set(['Risk', 'Issue']);

// Exported: CustomerProjectRegisters.js's read-only grid reuses these so the two views render type/
// status/level identically.
export const TYPE_BADGE = {
  Risk: 'badge-yellow',
  Issue: 'badge-red',
  Action: 'badge-blue',
  Assumption: 'badge-violet',
  Dependency: 'badge-gray',
  Decision: 'badge-green',
};

// Three statuses take an item off the open list. Kept as a set rather than a `!== 'Closed'` test so
// widening RAAIDD_STATUSES again can't silently start counting resolved items as open.
const RESOLVED_STATUSES = new Set(['Completed', 'Closed', 'Canceled']);

export function isResolved(status) {
  return RESOLVED_STATUSES.has(status);
}

// Not Started and Canceled stay gray so the eye skips them; the resolved states are green and the
// active ones blue.
export const STATUS_BADGE = {
  'Not Started': 'badge-gray',
  Open: 'badge-blue',
  'In Progress': 'badge-blue',
  Monitoring: 'badge-yellow',
  Completed: 'badge-green',
  Closed: 'badge-green',
  Canceled: 'badge-gray',
};

const LEVEL_BADGE = ['badge-green', 'badge-yellow', 'badge-red']; // index by the stored 0-2 value

export function levelLabel(value) {
  return value == null ? null : RAAIDD_LEVEL_LABELS[value] ?? null;
}

export function LevelBadge({ value }) {
  const label = levelLabel(value);
  if (label == null) return <span style={{ color: 'var(--text-light)' }}>—</span>;
  return <span className={`badge ${LEVEL_BADGE[value]}`}>{label}</span>;
}

// ---------- Risk matrix ----------

/**
 * The 3x3 matrix Level and Impact feed (Jeremy, 2026-09-03). `(level + 1) * (impact + 1)` over the
 * stored 0-2 scales gives 1, 2, 3, 4, 6 or 9 — banded back onto the same Low/Medium/High vocabulary
 * the inputs use, so the grid introduces no new words to learn.
 *
 * Derived here rather than stored: it is a reading of two columns, not a third fact about the row,
 * and a stored score would go stale the moment either input was edited. `probabilityPct` stays out
 * of it — the register keeps it as an independent free number, and folding it in would invent a
 * weighting the source template never had.
 *
 * Null unless the type is one the matrix applies to AND both ratings are present.
 */
export function severityScore(item) {
  if (!RATED_TYPES.has(item.type)) return null;
  if (item.level == null || item.level === '' || item.impact == null || item.impact === '') return null;
  return (Number(item.level) + 1) * (Number(item.impact) + 1);
}

export function severityBand(score) {
  if (score == null) return null;
  if (score <= 2) return 0; // Low
  if (score <= 4) return 1; // Medium
  return 2; // High
}

/**
 * One grid cell in place of the old Level / Impact / Prob % trio. Empty for every type outside
 * RATED_TYPES, since `severityScore` gates on it. The inputs live in the expand panel now that
 * editing is in-grid (item 1, 2026-09-12) rather than a separate form.
 */
export function SeverityBadge({ item }) {
  const score = severityScore(item);
  const band = severityBand(score);
  if (band == null) return <span style={{ color: 'var(--text-light)' }}>—</span>;
  const parts = [
    `Level ${levelLabel(item.level)}`,
    `Impact ${levelLabel(item.impact)}`,
    item.probabilityPct != null && item.probabilityPct !== '' ? `${item.probabilityPct}% likely` : null,
  ].filter(Boolean);
  return (
    <span className={`badge ${LEVEL_BADGE[band]}`} title={parts.join(' · ')}>
      {RAAIDD_LEVEL_LABELS[band]}
    </span>
  );
}

// ---------- Dates ----------

/**
 * A due or review date that has passed on an item nobody has resolved. String comparison on
 * `yyyy-mm-dd`, not Date arithmetic: both sides are calendar dates, and comparing them as dates is
 * how this codebase has introduced off-by-one-day bugs before (ADR 0009).
 */
export function isOverdue(dateValue, status) {
  if (!dateValue || isResolved(status)) return false;
  return toDateInput(dateValue) < todayDateInput();
}

function DateCell({ value, status }) {
  const overdue = isOverdue(value, status);
  return (
    <span
      style={overdue ? { color: 'var(--red)', fontWeight: 600 } : undefined}
      title={overdue ? 'Past due' : undefined}
    >
      {displayDate(value)}
    </span>
  );
}

/**
 * The newest note that hasn't been removed. The API returns them newest-first, so this is `[0]` most
 * of the time — but removed notes are in the list whenever "Show removed" is on, and a removed note
 * must never be what the grid reports as the row's latest update.
 */
export function latestUpdate(item) {
  return (item.updates || []).find((u) => !u.removedAt) ?? null;
}

// ---------- Payload ----------

const BLANK_ITEM = () => ({ type: 'Risk', description: 'New log item', status: 'Open', dateEntered: todayDateInput() });

// `level`/`impact`/the four dates are the fields whose DTO distinguishes "clear it" (explicit null)
// from "leave it alone" (omitted) -- see UpdateRiskRegisterItemDto. `row` is a live grid row (edited
// in place), always PATCHed, so there's nothing left to "leave alone" -- an empty box means "not set".
export function buildPayload(row) {
  const numOrClear = (v) => (v === '' || v == null ? null : Number(v));
  // Rounded, not just Number()'d -- the API's @IsInt() rejects a typed "33.3" outright.
  const intOrClear = (v) => (v === '' || v == null ? null : Math.round(Number(v)));
  const dateOrClear = (v) => (v ? v : null);
  // Only Risks and Issues carry a rating (RATED_TYPES). Reclassifying to a type that doesn't and
  // saving CLEARS the three rather than leaving them stored where nothing offers a way to see them.
  const rated = RATED_TYPES.has(row.type);
  const ratingOrClear = (parse) => (value) => (rated ? parse(value) : null);
  return {
    type: row.type,
    originalType: row.originalType || null,
    description: row.description.trim(),
    level: ratingOrClear(numOrClear)(row.level),
    impact: ratingOrClear(numOrClear)(row.impact),
    probabilityPct: ratingOrClear(intOrClear)(row.probabilityPct),
    areaCategory: (row.areaCategory || '').trim(),
    owner: (row.owner || '').trim(),
    status: row.status,
    internalOnly: !!row.internalOnly,
    dateEntered: toDateInput(row.dateEntered) || undefined,
    lastReviewedDate: dateOrClear(toDateInput(row.lastReviewedDate)),
    dateDue: dateOrClear(toDateInput(row.dateDue)),
    nextReviewDate: dateOrClear(toDateInput(row.nextReviewDate)),
    businessImpact: (row.businessImpact || '').trim(),
    triggerEvent: (row.triggerEvent || '').trim(),
    remediationPlan: (row.remediationPlan || '').trim(),
    contingencyPlan: (row.contingencyPlan || '').trim(),
    nextSteps: (row.nextSteps || '').trim(),
    dateClosed: dateOrClear(toDateInput(row.dateClosed)),
  };
}

// ---------- Sorting ----------

// Exported: CustomerProjectRegisters.js's grid has the same sortable columns and reuses this rather
// than keeping its own copy that could quietly drift from this one.
export const SORT_VALUE = {
  logNumber: (r) => r.logNumber,
  type: (r) => lower(r.type),
  description: (r) => lower(r.description),
  // -1 for an unrated row, so Risks and Issues with a matrix reading sort above the types that never
  // have one rather than tying with a genuine Low.
  risk: (r) => severityScore(r) ?? -1,
  owner: (r) => lower(r.owner),
  status: (r) => lower(r.status),
  // '' sorts before any real date, so ascending puts the undated at the top and descending puts the
  // furthest-out date there -- the two things you actually want from a due-date column.
  dateDue: (r) => toDateInput(r.dateDue),
  nextReviewDate: (r) => toDateInput(r.nextReviewDate),
  latestUpdate: (r) => toDateInput(latestUpdate(r)?.updateDate),
};

// ---------- Filters ----------

// Sentinels for the two options that aren't a plain equality test. Prefixed so they can't collide
// with a real status or a person's name.
export const UNRESOLVED_FILTER = '__unresolved';
export const UNASSIGNED_OWNER = '__unassigned';

// Plural keys and array values: every filter is multi-select (Jeremy, 2026-09-03). `showRemoved` is
// deliberately NOT here -- it changes what the API is asked for, not how the rows in hand are
// filtered.
export const BLANK_FILTERS = { types: [], statuses: [], owners: [], text: '' };

const FILTER_ARRAY_KEYS = ['types', 'statuses', 'owners'];

// Every free-text field, not just Description -- "what did we say we'd do about X" is as often in
// Next steps or the remediation plan as in the description itself. The update history is searched
// too, which is the point of keeping it: "who did we chase, and when".
const SEARCH_FIELDS = [
  'description', 'owner', 'areaCategory', 'remediationPlan', 'contingencyPlan',
  'nextSteps', 'businessImpact', 'triggerEvent',
];

function matchesText(item, needle) {
  if (SEARCH_FIELDS.some((f) => (item[f] || '').toLowerCase().includes(needle))) return true;
  return (item.updates || []).some((u) => (u.note || '').toLowerCase().includes(needle));
}

export function filterItems(items, filters) {
  const needle = (filters.text || '').trim().toLowerCase();
  return (items || []).filter((item) => {
    if (!matchesSelection(filters.types, item.type, item)) return false;
    if (!matchesSelection(filters.statuses, item.status, item, [
      { value: UNRESOLVED_FILTER, matches: (r) => !isResolved(r.status) },
    ])) return false;
    if (!matchesSelection(filters.owners, item.owner, item, [
      { value: UNASSIGNED_OWNER, matches: (r) => !r.owner },
    ])) return false;
    if (needle && !matchesText(item, needle)) return false;
    return true;
  });
}

export function isFiltered(filters) {
  return anyFilterSet(filters, FILTER_ARRAY_KEYS);
}

/** Owners actually present, so the dropdown can't offer a name that matches nothing. */
export function ownerOptions(items) {
  const names = new Set();
  let anyUnassigned = false;
  for (const item of items || []) {
    if (item.owner) names.add(item.owner);
    else anyUnassigned = true;
  }
  return { names: [...names].sort((a, b) => a.localeCompare(b)), anyUnassigned };
}

function Stat({ value, label, tone }) {
  return (
    <div className={`stat${tone ? ` is-${tone}` : ''}`}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/**
 * Its own full-width row, not a flex item inside the filter bar -- `.stat-row` is an
 * `auto-fit/minmax` grid, so nesting it in a flex row squeezes it to one column and the cards stack.
 * Counts describe the whole register, not the filtered subset; the grid reports the filtered count
 * itself, so the two can't be confused.
 *
 * Removed rows are excluded from every count even when they're on screen: they aren't on the
 * register any more, so counting them as unresolved work would overstate what's live. They get their
 * own card while they're being shown, so `Total` and the grid's "Showing X of Y" (which counts every
 * loaded row, removed included) visibly reconcile instead of silently disagreeing by the number
 * removed.
 */
export function RegisterSummary({ items }) {
  const live = items.filter((i) => !i.removedAt);
  const removed = items.length - live.length;
  const unresolved = live.filter((i) => !isResolved(i.status));
  const highSeverity = unresolved.filter((i) => severityBand(severityScore(i)) === 2);
  // "Needs a look" is the question the review dates exist to answer: past its due date, or past the
  // date someone said they'd next review it.
  const overdue = unresolved.filter(
    (i) => isOverdue(i.dateDue, i.status) || isOverdue(i.nextReviewDate, i.status),
  );
  return (
    <div className="stat-row">
      <Stat value={live.length} label="Total" />
      <Stat value={unresolved.length} label="Unresolved" />
      <Stat value={highSeverity.length} label="High risk, open" tone={highSeverity.length ? 'alert' : ''} />
      <Stat value={overdue.length} label="Overdue a look" tone={overdue.length ? 'alert' : ''} />
      {/* Only while removed rows are actually loaded -- a permanent "Removed: 0" card would be a
          column of nothing on every register that has never removed anything. */}
      {removed > 0 && <Stat value={removed} label="Removed" />}
    </div>
  );
}

export function RegisterFilters({ items, filters, onChange, showRemoved, onShowRemovedChange }) {
  const { names, anyUnassigned } = useMemo(() => ownerOptions(items), [items]);
  const set = (key) => (values) => onChange({ ...filters, [key]: values });

  const statusOptions = [
    // Ahead of the individual statuses because "what's still live" is the question the log gets
    // asked most, and it spans four of the seven values.
    { value: UNRESOLVED_FILTER, label: 'Unresolved (any)' },
    ...RAAIDD_STATUSES.map((s) => ({ value: s, label: s })),
  ];
  const ownerOpts = [
    ...(anyUnassigned ? [{ value: UNASSIGNED_OWNER, label: '(Unassigned)' }] : []),
    ...names.map((n) => ({ value: n, label: n })),
  ];

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <MultiSelect
        label="Type"
        allLabel="All types"
        options={RAAIDD_TYPES.map((t) => ({ value: t, label: t }))}
        selected={filters.types}
        onChange={set('types')}
        minWidth={150}
      />
      <MultiSelect
        label="Status"
        allLabel="All statuses"
        options={statusOptions}
        selected={filters.statuses}
        onChange={set('statuses')}
        minWidth={175}
      />
      <MultiSelect
        label="Owner"
        allLabel="All owners"
        options={ownerOpts}
        selected={filters.owners}
        onChange={set('owners')}
      />
      <SearchBox
        value={filters.text}
        onChange={(text) => onChange({ ...filters, text })}
        placeholder="Description, owner, plans, updates…"
      />
      {/* Only on the internal view: `onShowRemovedChange` is omitted on the customer portal, which
          must never be able to ask for removed rows (the API refuses anyway). */}
      {onShowRemovedChange && (
        <FilterCheckbox
          label="Show removed"
          checked={showRemoved}
          onChange={onShowRemovedChange}
          title="Removed items and removed review updates. Both stay on record and can be reinstated."
        />
      )}
      {isFiltered(filters) && (
        <button className="btn btn-outline" onClick={() => onChange({ ...BLANK_FILTERS })}>
          Clear filters
        </button>
      )}
    </div>
  );
}

// ---------- Page ----------

// A project's RAAIDD log (Risk / Action / Assumption / Issue / Dependency / Decision) -- replaces
// the "RAAIDD Register Template.xlsx" PMs currently maintain and email to customers directly. Full
// row parity with CustomerProjectRegisters.js apart from two per-row exceptions the API enforces:
// `internalOnly` rows and removed rows (see RiskRegisterService.findAll).
//
// Cells edit in place (item 1, 2026-09-12): the collapsed row's scannable fields (Type, Description,
// Owner, Status, Due, Next review) edit directly; the richer conditional fields (risk matrix, the
// four free-text plan fields, dates other than Due) live in the expand panel, reorganized into
// labeled groups (item 2) instead of one flat field grid. Add/Save live on the grid's own toolbar.
//
// One tab of ProjectRegisters.js since 2026-09-03, which owns the page header and the project
// picker -- hence `projectId` as a prop and `onCoverageChange` back up for the picker's ◇ marker.
// It's remounted on a project change (see the `key` there), so nothing here has to unwind the last
// project's filters or open form.
//
// Also rendered by CustomerProjectRegisters.js for a customer contact with edit rights on this
// project (item 5, 2026-09-12) -- `readOnly` collapses the toolbar and the grid's write affordances,
// same convention as the other four registers. `portal` is a separate signal from `readOnly`: a
// canEdit customer gets `readOnly=false` but is still in the portal, and two things stay staff-only
// regardless of their edit rights -- "Show removed" (the API always hides removed rows from an
// external caller, canEdit or not) and the internalOnly toggle itself (a staff editorial call, also
// rejected server-side -- see RiskRegisterService).
export default function RaaiddRegister({ projectId, onCoverageChange, readOnly = false, portal = false }) {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ ...BLANK_FILTERS });
  // Refetches rather than filtering in hand: the API leaves removed rows out by default, so they
  // aren't in the browser at all until asked for. Unchecked by default (Jeremy, 2026-09-03).
  const [showRemoved, setShowRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [sort, toggleSort] = useGridSort('logNumber');
  const confirmNavigation = useNavGuardConfirm();

  useRegisterDirty(dirtyIds.size > 0);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    apiFetch(`/risk-register-items?projectId=${projectId}${showRemoved ? '&includeRemoved=true' : ''}`)
      .then((rows) => {
        setItems(rows);
        // Reports coverage after every write, so logging the first item (or removing the last) moves
        // the picker's ◇ straight away rather than at the next reload. Removed rows don't count --
        // the API's coverage call excludes them too, so the two agree.
        onCoverageChange?.(rows.some((r) => !r.removedAt));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // `onCoverageChange` is a fresh closure on every ProjectRegisters render, so listing it here
    // would re-run the fetch on each of the parent's renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, showRemoved]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => filterItems(items, filters), [items, filters]);

  const sortedItems = useMemo(
    () => sortRows(filteredItems, sort, (row, key) => SORT_VALUE[key](row)),
    [filteredItems, sort],
  );

  function editCell(id, key, value) {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [key]: value } : i)));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Add and delete reload the grid from the server, which would discard other rows' unsaved edits,
  // so save them first -- same as Project Budgets' Add Line.
  async function flushDirty(skipId) {
    const ids = [...dirtyIds].filter((id) => id !== skipId);
    if (!ids.length) return;
    await Promise.all(ids.map((id) => apiFetch(`/risk-register-items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(items.find((r) => r.id === id))),
    })));
    setDirtyIds((prev) => { const next = new Set(prev); ids.forEach((id) => next.delete(id)); return next; });
  }

  async function addItem() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      await apiFetch('/risk-register-items', { method: 'POST', body: JSON.stringify({ ...BLANK_ITEM(), projectId }) });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy('');
    }
  }

  /** Removing is reversible; the ✕→✓? confirm carries that in its title rather than a popup. */
  async function removeItem(id) {
    setActionError('');
    try {
      await flushDirty(id);
      await apiFetch(`/risk-register-items/${id}/remove`, { method: 'POST' });
      // A dirty edit on this row is now moot -- left in place it would keep the row rendering as
      // "unsaved" after reload and keep the nav guard tripped for no reason.
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  async function reinstateItem(id) {
    setActionError('');
    try {
      await flushDirty();
      await apiFetch(`/risk-register-items/${id}/reinstate`, { method: 'POST' });
      load();
    } catch (e) {
      setActionError(e.message);
    }
  }

  /**
   * The four things you can do to a row's review history. Bundled so `RegisterGrid` takes one prop
   * rather than four, and so the customer view can simply not pass it.
   *
   * Each throws on failure -- `UpdateHistory` catches and shows the message next to the note it was
   * acting on -- and reloads the register on success, which is what re-renders the history.
   */
  // A ref because this memo depends only on `load`; a closed-over flushDirty would see stale edits.
  const flushRef = useRef(flushDirty);
  flushRef.current = flushDirty;
  const updateActions = useMemo(() => {
    const call = async (path, method, body) => {
      await flushRef.current();
      await apiFetch(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
      load();
    };
    return {
      add: (item, body) => call(`/risk-register-items/${item.id}/updates`, 'POST', body),
      edit: (update, body) => call(`/risk-register-items/updates/${update.id}`, 'PATCH', body),
      remove: (update) => call(`/risk-register-items/updates/${update.id}/remove`, 'POST'),
      reinstate: (update) => call(`/risk-register-items/updates/${update.id}/reinstate`, 'POST'),
    };
  }, [load]);

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      {/* The filter bar renders even on an empty register, because "Show removed" is the way
          back to a register whose every row has been removed. Not gated on `!loading` either: the
          refetch after every save would otherwise unmount it and collapse the page. */}
      {!!items.length && <RegisterSummary items={items} />}
      <div className="card" style={{ marginBottom: 16 }}>
        <RegisterFilters
          items={items}
          filters={filters}
          onChange={setFilters}
          showRemoved={showRemoved}
          // Omitted for a customer view (canEdit or not, so keyed off `portal` and NOT `readOnly`):
          // the API excludes removed rows for every external caller regardless of edit rights
          // (RiskRegisterService.findAll's externalSafe), so offering the control there would just be
          // a checkbox that visibly does nothing.
          onShowRemovedChange={portal ? undefined : (next) => { if (confirmNavigation()) setShowRemoved(next); }}
        />
      </div>

      <div className="card">
        {!readOnly && (
          <div className="sheet-toolbar" style={{ marginBottom: 10 }}>
            <div className="spacer" />
            <button className="btn btn-outline" onClick={addItem} disabled={!!busy}>
              {busy === 'adding' ? 'Adding…' : '+ Add item'}
            </button>
            <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
              {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
            </button>
          </div>
        )}
        {loading ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !items.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">✅</div>
            <div className="placeholder-title">Nothing logged yet</div>
            <div className="placeholder-sub">
              {readOnly
                ? 'Nothing has been logged on this register yet.'
                : (
                  <>
                    Add the first risk, action, assumption, issue, dependency or decision above
                    {/* "Show removed" is staff-only (see `portal` above) -- never mention it to a
                        customer, canEdit or not. */}
                    {!portal && !showRemoved && ' — or tick "Show removed" if you are looking for one that was removed'}.
                  </>
                )}
            </div>
          </div>
        ) : !sortedItems.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔍</div>
            <div className="placeholder-title">No items match these filters</div>
            <div className="placeholder-sub">
              {items.length} {items.length === 1 ? 'item is' : 'items are'} logged against this project — clear the filters to see them.
            </div>
          </div>
        ) : (
          <>
            {isFiltered(filters) && <FilterCount shown={sortedItems.length} total={items.length} />}
            <RegisterGrid
              items={sortedItems}
              sort={sort}
              onSort={toggleSort}
              expandedId={expandedId}
              onToggleExpand={(id) => setExpandedId((prev) => (prev === id ? null : id))}
              dirtyIds={dirtyIds}
              onEdit={editCell}
              onRemove={removeItem}
              onReinstate={reinstateItem}
              updateActions={updateActions}
              readOnly={readOnly}
              portal={portal}
            />
          </>
        )}
      </div>
    </div>
  );
}

const COLUMN_COUNT = 12;

/**
 * Exported: the customer portal renders the identical grid with `readOnly`, so the two views can't
 * drift on columns or on what hides behind the expand row.
 *
 * Column set (revised 2026-09-13): "Internal Only" (formerly the "Flags" column next to the expand
 * chevron, item 2) moved to the end of the row, alongside the other registers' trailing controls.
 */
export function RegisterGrid({
  items, sort, onSort, expandedId, onToggleExpand, dirtyIds, onEdit, onRemove, onReinstate, updateActions, readOnly,
  portal = false,
}) {
  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <SortTh label="#" sortKey="logNumber" sort={sort} onSort={onSort} style={{ width: 44 }} />
            <SortTh label="Type" sortKey="type" sort={sort} onSort={onSort} style={{ width: 130 }} />
            <SortTh label="Description" sortKey="description" sort={sort} onSort={onSort} style={{ minWidth: 220 }} />
            <SortTh
              label="Risk"
              sortKey="risk"
              sort={sort}
              onSort={onSort}
              style={{ width: 80 }}
              title="Level × Impact. Only Risks and Issues carry a rating; edit them in the row's detail (▸)."
            />
            <SortTh label="Owner" sortKey="owner" sort={sort} onSort={onSort} style={{ width: 130 }} />
            <SortTh label="Status" sortKey="status" sort={sort} onSort={onSort} style={{ width: 130 }} />
            <SortTh label="Due" sortKey="dateDue" sort={sort} onSort={onSort} style={{ width: 120 }} />
            <SortTh label="Next review" sortKey="nextReviewDate" sort={sort} onSort={onSort} style={{ width: 120 }} />
            <SortTh label="Latest update" sortKey="latestUpdate" sort={sort} onSort={onSort} style={{ minWidth: 175 }} />
            <th style={{ width: 46 }} title="Internal-only / removed">Internal Only</th>
            <th style={{ width: readOnly ? 12 : 36 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <RegisterRow
              key={item.id}
              item={item}
              index={i}
              dirty={dirtyIds?.has(item.id)}
              expanded={expandedId === item.id}
              onToggleExpand={() => onToggleExpand(item.id)}
              onEdit={onEdit}
              onRemove={() => onRemove(item.id)}
              onReinstate={() => onReinstate(item.id)}
              updateActions={updateActions}
              readOnly={readOnly}
              portal={portal}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RegisterRow({
  item, index, dirty, expanded, onToggleExpand, onEdit, onRemove, onReinstate, updateActions, readOnly,
  portal = false,
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const update = latestUpdate(item);
  const removed = !!item.removedAt;
  const edit = (key, value) => onEdit(item.id, key, value);

  return (
    <React.Fragment>
      {/* Removed rows stay legible but visibly out of play -- grayed rather than hidden, since they
          are only on screen at all because someone asked to see them. */}
      <tr className={rowClassName(index, dirty)} style={removed ? { opacity: 0.55 } : undefined}>
        <td className="cell-check">
          <button
            onClick={onToggleExpand}
            title={expanded ? 'Collapse' : 'Show detail'}
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-mid)' }}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td className="cell-num">{item.logNumber}</td>
        {readOnly ? (
          <td className="cell-read"><span className={`badge ${TYPE_BADGE[item.type] || 'badge-gray'}`}>{item.type}</span></td>
        ) : (
          <td>
            <select className="cell-select" value={item.type} onChange={(e) => edit('type', e.target.value)}>
              {RAAIDD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </td>
        )}
        {readOnly ? (
          <td className="cell-read" style={{ maxWidth: 320 }}>{item.description}</td>
        ) : (
          <td><AutoGrowTextarea value={item.description} onChange={(e) => edit('description', e.target.value)} /></td>
        )}
        <td className="cell-read"><SeverityBadge item={item} /></td>
        {readOnly ? (
          <td className="cell-read">{item.owner || '—'}</td>
        ) : (
          <td><input className="cell-input" value={item.owner || ''} onChange={(e) => edit('owner', e.target.value)} /></td>
        )}
        {readOnly ? (
          <td className="cell-read"><span className={`badge ${STATUS_BADGE[item.status] || 'badge-gray'}`}>{item.status}</span></td>
        ) : (
          <td>
            <select className="cell-select" value={item.status} onChange={(e) => edit('status', e.target.value)}>
              {RAAIDD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </td>
        )}
        {readOnly ? (
          <td className="cell-read"><DateCell value={item.dateDue} status={item.status} /></td>
        ) : (
          <td><input type="date" className="cell-input" value={toDateInput(item.dateDue)} onChange={(e) => edit('dateDue', e.target.value)} /></td>
        )}
        {readOnly ? (
          <td className="cell-read"><DateCell value={item.nextReviewDate} status={item.status} /></td>
        ) : (
          <td><input type="date" className="cell-input" value={toDateInput(item.nextReviewDate)} onChange={(e) => edit('nextReviewDate', e.target.value)} /></td>
        )}
        <td className="cell-read" style={{ maxWidth: 280 }}>
          {update ? (
            <>
              <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
                {displayDate(update.updateDate)}
                {update.author?.name ? ` · ${update.author.name}` : ''}
              </div>
              {/* Clamped rather than truncated with an ellipsis: two lines of an update usually carry
                  the point, and the full text is one click away in the expand panel. */}
              <div style={{
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', whiteSpace: 'pre-wrap',
              }}>
                {update.note}
              </div>
            </>
          ) : (
            <span style={{ color: 'var(--text-light)' }}>No updates yet</span>
          )}
        </td>
        <td className="cell-check">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Staff-only regardless of `readOnly`: a canEdit customer still can't mark a row
                internal-only (RiskRegisterService rejects it too), so this stays keyed off `portal`. */}
            {portal ? (
              item.internalOnly && <span className="badge badge-violet" title="Hidden from the customer portal">Int.</span>
            ) : (
              <input
                type="checkbox" checked={!!item.internalOnly} title="Internal only — hide from the customer portal"
                onChange={(e) => edit('internalOnly', e.target.checked)}
              />
            )}
            {removed && <span className="badge badge-gray" title={`Removed ${displayDate(item.removedAt)}`}>Rem.</span>}
          </div>
        </td>
        <td className="cell-check">
          {!readOnly && (
            removed ? (
              <button className="icon-btn" onClick={onReinstate} title="Reinstate this item">↺</button>
            ) : confirmRemove ? (
              <button
                className="icon-btn"
                onClick={() => { setConfirmRemove(false); onRemove(); }}
                title="Confirm — it stays on record and can be reinstated from &quot;Show removed&quot;"
              >
                ✓?
              </button>
            ) : (
              <button className="icon-btn" onClick={() => setConfirmRemove(true)} title="Remove this item (reversible)">✕</button>
            )
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={COLUMN_COUNT} style={{ background: 'var(--light-bg)' }}>
            <DetailPanel item={item} onEdit={edit} readOnly={readOnly} />
            <UpdateHistory
              item={item}
              actions={updateActions}
              readOnly={readOnly || !!item.removedAt}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

/**
 * The expand panel, reorganized (item 2, 2026-09-12) into three labeled groups instead of one flat
 * auto-fit field grid whose shape used to jump around depending on which conditional fields applied:
 * Dates, Classification (incl. the risk matrix, only for Risks/Issues), then Impact & Notes.
 */
function DetailPanel({ item, onEdit, readOnly }) {
  const showRatings = RATED_TYPES.has(item.type);

  return (
    <div style={{ padding: '12px 8px' }}>
      <DetailGroup label="Dates">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
          <DetailField label="Entered">
            {readOnly ? displayDate(item.dateEntered) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.dateEntered)} onChange={(e) => onEdit('dateEntered', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Last reviewed">
            {readOnly ? displayDate(item.lastReviewedDate) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.lastReviewedDate)} onChange={(e) => onEdit('lastReviewedDate', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Date closed">
            {readOnly ? displayDate(item.dateClosed) : (
              <input type="date" className="cell-input" style={{ padding: '4px 0' }} value={toDateInput(item.dateClosed)} onChange={(e) => onEdit('dateClosed', e.target.value)} />
            )}
          </DetailField>
        </div>
      </DetailGroup>

      <DetailGroup label="Classification" style={{ marginTop: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: showRatings ? 14 : 0 }}>
          <DetailField label="Area / Category">
            {readOnly ? (item.areaCategory || '—') : (
              <input className="cell-input" style={{ padding: '4px 0' }} value={item.areaCategory || ''} onChange={(e) => onEdit('areaCategory', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Original RAAIDD type (if reclassified)">
            {readOnly ? (item.originalType || '—') : (
              <select className="cell-select" style={{ padding: '4px 0' }} value={item.originalType || ''} onChange={(e) => onEdit('originalType', e.target.value)}>
                <option value="">—</option>
                {RAAIDD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </DetailField>
        </div>
        {/* Only Risks and Issues carry a rating -- shown for every other type only if a stale value is
            still present (e.g. left over from a reclassification), same rule the grid's Risk cell and
            severityScore follow. */}
        {(showRatings || item.level != null || item.impact != null || item.probabilityPct != null) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
            <DetailField label="Level">
              {readOnly || !showRatings ? levelLabel(item.level) ?? '—' : (
                <select className="cell-select" style={{ padding: '4px 0' }} value={item.level ?? ''} onChange={(e) => onEdit('level', e.target.value)}>
                  <option value="">—</option>
                  {RAAIDD_LEVEL_LABELS.map((label, idx) => <option key={label} value={idx}>{label}</option>)}
                </select>
              )}
            </DetailField>
            <DetailField label="Impact">
              {readOnly || !showRatings ? levelLabel(item.impact) ?? '—' : (
                <select className="cell-select" style={{ padding: '4px 0' }} value={item.impact ?? ''} onChange={(e) => onEdit('impact', e.target.value)}>
                  <option value="">—</option>
                  {RAAIDD_LEVEL_LABELS.map((label, idx) => <option key={label} value={idx}>{label}</option>)}
                </select>
              )}
            </DetailField>
            <DetailField label="Probability %">
              {readOnly || !showRatings ? (item.probabilityPct != null && item.probabilityPct !== '' ? `${item.probabilityPct}%` : '—') : (
                <input type="number" min="0" max="100" className="cell-input" style={{ padding: '4px 0' }} value={item.probabilityPct ?? ''} onChange={(e) => onEdit('probabilityPct', e.target.value)} />
              )}
            </DetailField>
            {!showRatings && !readOnly && (item.level != null || item.impact != null || item.probabilityPct != null) && (
              <DetailField label=" ">
                <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
                  Cleared on save — {item.type} doesn&apos;t carry a rating.
                </span>
              </DetailField>
            )}
          </div>
        )}
      </DetailGroup>

      <DetailGroup label="Impact &amp; notes" style={{ marginTop: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <DetailField label="Business / project impact">
            {readOnly ? (item.businessImpact || '—') : (
              <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.businessImpact || ''} onChange={(e) => onEdit('businessImpact', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Trigger event / indicator">
            {readOnly ? (item.triggerEvent || '—') : (
              <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.triggerEvent || ''} onChange={(e) => onEdit('triggerEvent', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Remediation / response">
            {readOnly ? (item.remediationPlan || '—') : (
              <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.remediationPlan || ''} onChange={(e) => onEdit('remediationPlan', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Contingency plan">
            {readOnly ? (item.contingencyPlan || '—') : (
              <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.contingencyPlan || ''} onChange={(e) => onEdit('contingencyPlan', e.target.value)} />
            )}
          </DetailField>
          <DetailField label="Pending / next steps / resolution">
            {readOnly ? (item.nextSteps || '—') : (
              <AutoGrowTextarea style={{ background: 'var(--surface)', borderRadius: 'var(--radius)' }} value={item.nextSteps || ''} onChange={(e) => onEdit('nextSteps', e.target.value)} />
            )}
          </DetailField>
        </div>
      </DetailGroup>
    </div>
  );
}

function DetailGroup({ label, children, style }) {
  return (
    <div style={style}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-mid)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}

/**
 * The full review history, newest first, with the form that appends to it and per-note correct /
 * remove / reinstate (Jeremy, 2026-09-03). Unchanged by the 2026-09-12 inline-editing rework -- it
 * already saves each note immediately and independently of the row's own batched Save, which is
 * called out below so nobody loses one expecting the row's Save button to cover it too.
 *
 * Read-only for a customer, and for a removed item: a row that's off the register shouldn't be
 * gathering new commentary; reinstate it first.
 */
export function UpdateHistory({ item, actions, readOnly, compact }) {
  const [note, setNote] = useState('');
  const [updateDate, setUpdateDate] = useState(todayDateInput());
  const [nextReviewDate, setNextReviewDate] = useState('');
  // { id, note, updateDate } while one note is being corrected. Only ever one at a time.
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const updates = item.updates || [];

  /** Every action reloads the register on success and surfaces its own failure in place. */
  async function run(fn) {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submitNew() {
    const ok = await run(() => actions.add(item, {
      note: note.trim(),
      updateDate: updateDate || undefined,
      nextReviewDate: nextReviewDate || undefined,
    }));
    // Only cleared on success, so a failed save doesn't lose what was typed.
    if (ok) {
      setNote('');
      setNextReviewDate('');
    }
  }

  async function saveEdit() {
    const ok = await run(() => actions.edit(editing, {
      note: editing.note.trim(),
      updateDate: editing.updateDate || undefined,
    }));
    if (ok) setEditing(null);
  }

  return (
    <div style={{ padding: compact ? 0 : '4px 8px 14px' }}>
      {!compact && (
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-mid)', marginBottom: 6 }}>
          Review updates
        </div>
      )}
      <ErrorBanner error={error} />
      {!updates.length ? (
        <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 10 }}>
          Nothing recorded yet. An update is how the register shows this is being actively managed.
        </div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          {updates.map((u) => (
            <UpdateNote
              key={u.id}
              update={u}
              readOnly={readOnly}
              busy={busy}
              editing={editing?.id === u.id ? editing : null}
              onStartEdit={() => setEditing({
                id: u.id,
                note: u.note,
                updateDate: toDateInput(u.updateDate),
              })}
              onChangeEdit={(patch) => setEditing((prev) => ({ ...prev, ...patch }))}
              onCancelEdit={() => setEditing(null)}
              onSaveEdit={saveEdit}
              onRemove={() => run(() => actions.remove(u))}
              onReinstate={() => run(() => actions.reinstate(u))}
            />
          ))}
        </div>
      )}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
            <label className="form-label">Add an update</label>
            <textarea
              className="form-input"
              rows={2}
              placeholder="What has happened, and what happens next…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, width: 150 }}>
            <label className="form-label">Update date</label>
            <input type="date" className="form-input" value={updateDate} onChange={(e) => setUpdateDate(e.target.value)} />
          </div>
          {/* Set here as well as on the row itself, because scheduling the next look is part of the
              same act as writing up this one -- the API writes both in one request. */}
          <div className="form-group" style={{ marginBottom: 0, width: 150 }}>
            <label className="form-label">Next review</label>
            <input type="date" className="form-input" value={nextReviewDate} onChange={(e) => setNextReviewDate(e.target.value)} />
          </div>
          <button
            className="btn btn-primary"
            style={{ marginBottom: 1 }}
            disabled={busy || !note.trim()}
            onClick={submitNew}
          >
            {busy ? 'Saving…' : 'Add update'}
          </button>
        </div>
      )}
    </div>
  );
}

/** One note: its own read view, its own inline correction form. */
function UpdateNote({
  update, readOnly, busy, editing, onStartEdit, onChangeEdit, onCancelEdit, onSaveEdit,
  onRemove, onReinstate,
}) {
  const removed = !!update.removedAt;

  if (editing) {
    return (
      <div style={{ borderLeft: '2px solid var(--primary-light)', padding: '2px 0 8px 10px', marginBottom: 6 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 240 }}>
            <label className="form-label">Correct this update</label>
            <textarea
              className="form-input"
              rows={2}
              value={editing.note}
              onChange={(e) => onChangeEdit({ note: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, width: 150 }}>
            <label className="form-label">Update date</label>
            <input
              type="date"
              className="form-input"
              value={editing.updateDate}
              onChange={(e) => onChangeEdit({ updateDate: e.target.value })}
            />
          </div>
          <button
            className="btn btn-primary"
            style={{ fontSize: 11, padding: '5px 10px', marginBottom: 1 }}
            disabled={busy || !editing.note.trim()}
            onClick={onSaveEdit}
          >
            Save
          </button>
          <button
            className="btn btn-outline"
            style={{ fontSize: 11, padding: '5px 10px', marginBottom: 1 }}
            disabled={busy}
            onClick={onCancelEdit}
          >
            Cancel
          </button>
        </div>
        {/* Editing the date here does not move the item's Last reviewed -- that's a field on the row
            itself, and re-deriving it from a typo fix would overwrite a date someone set by hand. */}
        <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
          Correcting an update doesn't change the row's Last reviewed date — edit that in the row's own detail.
        </div>
      </div>
    );
  }

  return (
    // Grayed rather than hidden: a removed note is only on screen because someone asked to see it.
    <div
      style={{
        borderLeft: '2px solid var(--border)', padding: '2px 0 8px 10px', marginBottom: 6,
        opacity: removed ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, color: 'var(--text-light)' }}>
          {displayDate(update.updateDate)}{update.author?.name ? ` · ${update.author.name}` : ''}
        </div>
        {removed && (
          <span className="badge badge-gray" title={`Removed ${displayDate(update.removedAt)}`}>
            Removed
          </span>
        )}
        {!readOnly && (
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {removed ? (
              <button className="btn btn-outline" style={{ fontSize: 11, padding: '2px 7px' }} disabled={busy} onClick={onReinstate}>
                Reinstate
              </button>
            ) : (
              <>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '2px 7px' }} disabled={busy} onClick={onStartEdit}>
                  Edit
                </button>
                <button
                  className="btn btn-outline"
                  style={{ fontSize: 11, padding: '2px 7px' }}
                  disabled={busy}
                  onClick={() => {
                    if (window.confirm('Remove this update?\n\nIt stays on record and can be reinstated from "Show removed".')) {
                      onRemove();
                    }
                  }}
                >
                  Remove
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{update.note}</div>
    </div>
  );
}
